import { Box, Card, CardContent, CircularProgress, Skeleton, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

/** Shared message shown in the center of the skeleton. */
function MediaInProgressMessage() {
  return (
    <Typography variant="body2" color="text.secondary">
      <FormattedMessage
        id="fragments.mediaStatus.waiting"
        defaultMessage="Generación de media en proceso"
      />
    </Typography>
  );
}

/** Same wrapper as VideoFragmentPlayerContent so skeleton matches player dimensions. */
const videoPlayerWrapperSx = {
  p: 2,
  width: { lg: '100%', xl: '90%' },
  margin: '0 auto',
} as const;

/**
 * Video-shaped skeleton matching the fragment video player layout and aspect ratio.
 * Spinner and message are centered over the skeleton.
 */
function VideoSkeleton() {
  return (
    <Box sx={videoPlayerWrapperSx}>
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9' }}>
        <Skeleton
          variant="rectangular"
          sx={{ width: '100%', height: '100%', borderRadius: 1 }}
          animation="wave"
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={16} />
          <MediaInProgressMessage />
        </Box>
      </Box>
    </Box>
  );
}

/** Waveform height used by ChunkStream so audio skeleton matches real chunk player. */
const WAVEFORM_HEIGHT = 58;

/**
 * Audio-shaped skeleton matching the fragment chunk player: card with header and waveform bar.
 * Spinner and message are centered over the waveform skeleton.
 */
function AudioSkeleton() {
  return (
    <Box sx={{ mb: 3 }}>
      <Card sx={{ overflow: 'visible' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              position: 'relative',
              height: WAVEFORM_HEIGHT,
            }}
          >
            <Skeleton variant="rounded" width={40} height={40} sx={{ flexShrink: 0 }} />
            <Skeleton
              variant="rectangular"
              sx={{ flex: 1, height: WAVEFORM_HEIGHT, borderRadius: 1 }}
              animation="wave"
            />
            <Box
              sx={{
                position: 'absolute',
                left: 48,
                right: 0,
                top: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <MediaInProgressMessage />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * Loading placeholder shown in the fragment view when media is not ready yet
 * (status WAITING or IN_PROGRESS). Renders a skeleton matching the media type:
 * video player dimensions (16:9) or audio chunk player (card + waveform bar).
 */
export function MediaInProgress(props: { variant: 'video' | 'audio' }) {
  const { variant } = props;
  return variant === 'video' ? <VideoSkeleton /> : <AudioSkeleton />;
}
